import { colors as themeColors } from '../../../utils/theme';
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogActions, Button, TextField, FormControl, MenuItem, Select, InputLabel, Box, Typography, Stack, Chip, IconButton, Alert, } from "@mui/material";
import { UploadFile, Delete, CheckCircle } from "@mui/icons-material";
import dayjs from "dayjs";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const inputProps = { size: "small", sx: { height: 40 } };

const englishOptions = ["No English", "medium English", "Good English", "Fluent English"];

export default function HRDocumentsPreferences({ formData, documents, pendingFiles, previews, handleFileSelect, removeFile, connectionString, handleChange, setFormData }) {

    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [photoModalUrl, setPhotoModalUrl] = useState("");

    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': {
            color: 'red',
        },
    };

    // ✅ VALIDATION HELPER FUNCTION
    const isSalaryValid = (salary) => {
        if (!salary || String(salary).length === 0) return true; // Empty is OK
        return Number(salary) >= 0;
    };

    return (
        <>
            <Stack spacing={2} marginTop={5}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <FormControl {...inputProps} sx={{ flex: 1, height: 40 }}>
                        <InputLabel>English Proficiency</InputLabel>
                        <Select value={formData.englishProficiency} label="English Proficiency" onChange={handleChange("englishProficiency")} sx={{ height: 40 }} >
                            {englishOptions.map((o) => (
                                <MenuItem key={o} value={o}>{o}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField label="Languages Known" placeholder="Hindi, English..." {...inputProps} sx={{ flex: 1 }} value={formData.languagesKnown} onChange={handleChange("languagesKnown")} />
                </Box>

                {/* ROW 2: Salary Expected + Notice Period + Joining Date */}
                <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField type="number" label="Salary Expected" placeholder="Enter expected salary" {...inputProps} sx={{ flex: 1 }} value={formData.salaryExpected}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || Number(value) >= 0) {
                                setFormData(prev => ({ ...prev, salaryExpected: value }));
                            }
                        }}
                        inputProps={{ min: 0 }}
                        error={!isSalaryValid(formData.salaryExpected)}
                        helperText={!isSalaryValid(formData.salaryExpected) ? "Salary cannot be negative" : ""}
                    />

                    <FormControl {...inputProps} sx={{ flex: 1, height: 40 }}>
                        <InputLabel>Notice Period</InputLabel>
                        <Select value={formData.noticePeriod} label="Notice Period" onChange={handleChange("noticePeriod")} sx={{ height: 40 }} >
                            {["NIL", "7-15 days", "14 days", "1 Month & Above"].map(option => (
                                <MenuItem key={option} value={option}> {option} </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <DatePicker label="Preferred Joining Date" value={formData.joiningDate ? dayjs(formData.joiningDate) : null} onChange={(newValue) => setFormData(p => ({ ...p, joiningDate: newValue ? newValue.format("YYYY-MM-DD") : "" }))} format="DD/MM/YYYY" slotProps={{ textField: { ...inputProps, sx: { flex: 1 } } }} />
                </Box>

                {/* Resume, Photo, and NEW: Identity Proof */}
                {["Resume", "Photo", "IdProof"].map((type) => {
                    const isPhoto = type === "Photo" || type === "IdProof";
                    const existingDoc = documents.find(d => d.DocumentType === type);

                    return (
                        <Box key={type} p={3} border={`2px dashed ${themeColors.primary.main}`} borderRadius={2} bgcolor="#f8fbff">
                            <Typography variant="h6" gutterBottom>
                                {type === "Resume" && (
                                    <>
                                        Resume Upload{" "}
                                        <span style={{ color: themeColors.error.dark, fontWeight: 500 }}>*</span>
                                    </>
                                )}

                                {type === "Photo" && (
                                    <>
                                        Photo Upload{" "}
                                        <span style={{ color: themeColors.error.dark, fontWeight: 500 }}>*</span>
                                    </>
                                )}

                                {type === "IdProof" && "Identity Proof (Aadhaar, PAN, etc.)"}
                            </Typography>


                            {/* Existing file from DB */}
                            {existingDoc && (
                                <Box mb={2}>
                                    {isPhoto ? (
                                        <Box component="img" src={existingDoc.downloadUrl || "/placeholder.png"} alt={`${type} preview`} sx={{ maxWidth: 200, height: "auto", borderRadius: 2, cursor: existingDoc.downloadUrl ? "zoom-in" : "not-allowed", border: `3px solid ${themeColors.neutral.line}`, boxShadow: 3, opacity: existingDoc.downloadUrl ? 1 : 0.5, }}
                                            onClick={() => {
                                                if (existingDoc.downloadUrl) {
                                                    setPhotoModalUrl(existingDoc.downloadUrl);
                                                    setPhotoModalOpen(true);
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Chip label={existingDoc.FileName} color={existingDoc.downloadUrl ? "success" : "warning"} clickable={!!existingDoc.downloadUrl} sx={{ cursor: existingDoc.downloadUrl ? "pointer" : "default" }}
                                            onClick={() => {
                                                if (!existingDoc.downloadUrl) return;
                                                try {
                                                    const base64 = existingDoc.downloadUrl.split(",")[1];
                                                    const byteCharacters = atob(base64);
                                                    const byteArray = Uint8Array.from(byteCharacters, c => c.charCodeAt(0));
                                                    const blob = new Blob([byteArray], { type: "application/pdf" });
                                                    const url = URL.createObjectURL(blob);
                                                    window.open(url, "_blank");
                                                    setTimeout(() => URL.revokeObjectURL(url), 10000);
                                                } catch (err) {
                                                    console.error("Failed to open file:", err);
                                                }
                                            }}
                                        />
                                    )}

                                    {!existingDoc.downloadUrl && (
                                        <Alert severity="warning" sx={{ mt: 1, fontSize: "0.85rem" }}>
                                            {type === "IdProof" ? "Identity proof" : type} not accessible. Please re-upload.
                                        </Alert>
                                    )}
                                </Box>
                            )}

                            {/* New file preview */}
                            {pendingFiles[type] && (
                                <Box mt={2}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CheckCircle color="success" />
                                        <Typography fontWeight="bold">{pendingFiles[type].name}</Typography>
                                        <IconButton size="small" onClick={() => removeFile(type)}>
                                            <Delete />
                                        </IconButton>
                                    </Box>

                                    {/* Preview image or first page of PDF */}
                                    {previews[type]?.type === "image" && (
                                        <Box component="img" src={previews[type].url} alt="Preview" sx={{ maxWidth: 200, mt: 2, borderRadius: 2, cursor: "zoom-in" }} onClick={() => { setPhotoModalUrl(previews[type].url); setPhotoModalOpen(true); }} />
                                    )}
                                </Box>
                            )}

                            {/* Upload Button */}
                            <input accept={type === "Resume" ? ".pdf,.doc,.docx" : "image/*,.pdf"} style={{ display: "none" }} id={`upload-${type}`} type="file" onChange={(e) => handleFileSelect(e.target.files[0], type)} />
                            <label htmlFor={`upload-${type}`}>
                                <Button variant="outlined" component="span" startIcon={<UploadFile />}>
                                    {pendingFiles[type] || existingDoc ? "Replace" : "Choose"} {type === "IdProof" ? "ID Proof" : type}
                                </Button>
                            </label>
                        </Box>
                    );
                })}

                {/* Photo / ID Proof Modal */}
                <Dialog open={photoModalOpen} onClose={() => setPhotoModalOpen(false)} maxWidth="lg" fullWidth>
                    <DialogContent sx={{ p: 0, bgcolor: "black", textAlign: "center" }}>
                        <img src={photoModalUrl} alt="Full size" style={{ maxWidth: "100%", maxHeight: "90vh", objectFit: "contain" }} onError={(e) => { e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjY2NjIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjNjY2Ij5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+"; }} />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: "center", bgcolor: "rgba(0,0,0,0.8)" }}>
                        <Button onClick={() => setPhotoModalOpen(false)} variant="contained" color="primary"> Close </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </>
    );
}